// react
import { useEffect, useRef } from 'react';

// internationalization
import { useTranslation } from 'react-i18next';

// tanstack
import { useRouter } from '@tanstack/react-router';
import type { ErrorComponentProps } from '@tanstack/react-router';

// material ui
import { Button, Typography } from '@mui/material';
import Box from '@mui/material/Box';

// shared components
import { appInsights } from '@/components/application-insights/ApplicationInsightsService';

function toError(error: unknown) {
  return error instanceof Error ? error : new Error(String(error));
}

function ErrorBoundary({ error }: ErrorComponentProps) {
  const { t } = useTranslation('app');
  const { history } = useRouter();

  // Store the first error this fallback was rendered for.
  // This lets the tracking effect run once without depending on a changing error object.
  const errorRef = useRef(error);

  useEffect(() => {
    const exception = toError(errorRef.current);

    try {
      appInsights.trackException({
        exception,
        properties: {
          source: 'TanStack Router errorComponent',
          url: window.location.href,
        },
      });
    } catch (trackingError) {
      console.warn('Failed to track ErrorBoundary exception:', trackingError);
    }
  }, []);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '80vh',
      }}
    >
      <div>
        <img
          className="error-image"
          src="https://cdn-icons-png.flaticon.com/512/4539/4539472.png"
          alt=""
          style={{ height: '150px' }}
        />
      </div>

      <Typography variant="h4">{t('errors.unexpected_error')}</Typography>

      <Box sx={{ mt: 1 }}>
        <Button onClick={() => history.go(-1)}>{t('errors.go_back')}</Button>
      </Box>
    </Box>
  );
}

export default ErrorBoundary;
